/// Transcription of PixelBorderPainter from
/// mobile/lib/ui/pixel/pixel_container.dart.
///
/// The shape is drawn on a grid of `pixelScale`-sized cells: a straight-edged
/// body, quarter-disc corner fills, and a one-cell outline whose corners are
/// Bresenham arcs. CSS border-radius cannot produce this, so the algorithm is
/// reproduced cell for cell on a canvas.
///
/// `radius` is in grid cells, not pixels, exactly as in the Dart original.

export interface PixelBorderOptions {
  radius: number;
  pixelScale: number;
  borderColor: string;
  fillColor: string;
  topColor?: string | null;
  bottomColor?: string | null;
}

type Corner = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

export function paintPixelBorder(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  options: PixelBorderOptions,
): void {
  const scale = options.pixelScale;
  const gridW = Math.max(1, Math.floor(width / scale));
  const gridH = Math.max(1, Math.floor(height / scale));

  const maxRadius = Math.max(0, Math.floor((Math.min(gridW, gridH) - 1) / 2));
  const r = Math.min(Math.max(options.radius, 0), maxRadius);

  const isSplit = options.topColor != null || options.bottomColor != null;
  const topPaint = options.topColor ?? options.fillColor;
  const bottomPaint = options.bottomColor ?? options.fillColor;

  ctx.clearRect(0, 0, width, height);

  const plotCell = (x: number, y: number, color: string): void => {
    ctx.fillStyle = color;
    ctx.fillRect(x * scale, y * scale, scale, scale);
  };

  const fillRectCells = (x: number, y: number, w: number, h: number, color: string): void => {
    if (w <= 0 || h <= 0) return;
    ctx.fillStyle = color;
    ctx.fillRect(x * scale, y * scale, w * scale, h * scale);
  };

  // --- body fill (axis-aligned cross; corners filled separately) ---
  if (isSplit) {
    const mid = Math.floor(gridH / 2);
    fillRectCells(r, 0, gridW - 2 * r, mid, topPaint);
    fillRectCells(r, mid, gridW - 2 * r, gridH - mid, bottomPaint);
    fillRectCells(0, r, r, mid - r, topPaint);
    fillRectCells(gridW - r, r, r, mid - r, topPaint);
    fillRectCells(0, mid, r, gridH - mid - r, bottomPaint);
    fillRectCells(gridW - r, mid, r, gridH - mid - r, bottomPaint);
  } else {
    fillRectCells(r, 0, gridW - 2 * r, gridH, options.fillColor);
    fillRectCells(0, r, gridW, gridH - 2 * r, options.fillColor);
  }

  // --- filled corners (one horizontal span per row) ---
  const fillCorner = (
    cx: number,
    cy: number,
    cornerRadius: number,
    color: string,
    corner: Corner,
  ): void => {
    if (cornerRadius <= 0) return;
    for (let dy = 0; dy <= cornerRadius; dy += 1) {
      const span = Math.floor(Math.sqrt(cornerRadius * cornerRadius - dy * dy));
      switch (corner) {
        case 'topLeft':
          fillRectCells(cx - span, cy - dy, span + 1, 1, color);
          break;
        case 'topRight':
          fillRectCells(cx, cy - dy, span + 1, 1, color);
          break;
        case 'bottomLeft':
          fillRectCells(cx - span, cy + dy, span + 1, 1, color);
          break;
        case 'bottomRight':
          fillRectCells(cx, cy + dy, span + 1, 1, color);
          break;
      }
    }
  };

  const topFill = isSplit ? topPaint : options.fillColor;
  const bottomFill = isSplit ? bottomPaint : options.fillColor;
  fillCorner(r, r, r, topFill, 'topLeft');
  fillCorner(gridW - r - 1, r, r, topFill, 'topRight');
  fillCorner(r, gridH - r - 1, r, bottomFill, 'bottomLeft');
  fillCorner(gridW - r - 1, gridH - r - 1, r, bottomFill, 'bottomRight');

  // --- border outline (Bresenham arcs + straight edges) ---
  const strokeCorner = (cx: number, cy: number, cornerRadius: number, corner: Corner): void => {
    if (cornerRadius <= 0) return;
    let x = 0;
    let y = cornerRadius;
    let d = 3 - 2 * cornerRadius;

    const plot = (px: number, py: number): void => {
      if (px < 0 || py < 0) return;
      switch (corner) {
        case 'topLeft':
          plotCell(cx - px, cy - py, options.borderColor);
          break;
        case 'topRight':
          plotCell(cx + px, cy - py, options.borderColor);
          break;
        case 'bottomLeft':
          plotCell(cx - px, cy + py, options.borderColor);
          break;
        case 'bottomRight':
          plotCell(cx + px, cy + py, options.borderColor);
          break;
      }
    };

    while (y >= x) {
      plot(x, y);
      plot(y, x);
      x += 1;
      if (d > 0) {
        y -= 1;
        d = d + 4 * (x - y) + 10;
      } else {
        d = d + 4 * x + 6;
      }
    }
  };

  strokeCorner(r, r, r, 'topLeft');
  strokeCorner(gridW - r - 1, r, r, 'topRight');
  strokeCorner(r, gridH - r - 1, r, 'bottomLeft');
  strokeCorner(gridW - r - 1, gridH - r - 1, r, 'bottomRight');

  fillRectCells(r, 0, gridW - 2 * r, 1, options.borderColor);
  fillRectCells(r, gridH - 1, gridW - 2 * r, 1, options.borderColor);
  fillRectCells(0, r, 1, gridH - 2 * r, options.borderColor);
  fillRectCells(gridW - 1, r, 1, gridH - 2 * r, options.borderColor);
}
